import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Container, Header, Grid, Icon, Divider } from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import LoginForm from '../users/LoginForm';
import RegisterForm from '../users/RegisterForm';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const useIntersectionObserver = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, ...options }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
};

const useCounter = (target: number, isVisible: boolean, duration = 1500) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, target, duration]);

  return count;
};

// ---------------------------------------------------------------------------
// CyclingPills
// ---------------------------------------------------------------------------

const CyclingPills: React.FC<{ pills: string[] }> = ({ pills }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setExiting(true);
      setTimeout(() => {
        setActiveIndex(prev => (prev + 1) % pills.length);
        setExiting(false);
      }, 350);
    }, 2800);
    return () => clearInterval(interval);
  }, [pills.length]);

  return (
    <div
      style={{
        height: '44px',
        position: 'relative',
        marginBottom: '0.5em',
      }}
    >
      <div
        key={activeIndex}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: exiting
            ? 'translate(-50%, calc(-50% - 8px))'
            : 'translate(-50%, -50%)',
          opacity: exiting ? 0 : 1,
          transition: 'opacity 0.35s ease, transform 0.35s ease',
          background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: '50px',
          padding: '0.5em 1.2em',
          color: 'rgba(255,255,255,0.95)',
          fontSize: 'clamp(0.82em, 2.2vw, 0.96em)',
          fontWeight: 500,
          letterSpacing: '0.01em',
          whiteSpace: 'nowrap',
          backdropFilter: 'blur(8px)',
        }}
      >
        {pills[activeIndex]}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// StatBadge
// ---------------------------------------------------------------------------

interface StatBadgeProps {
  icon: string;
  value: number | string;
  label: string;
  isVisible: boolean;
  target?: number;
  delay?: number;
}

const StatBadge: React.FC<StatBadgeProps> = ({
  icon,
  value,
  label,
  isVisible,
  target,
  delay = 0,
}) => {
  const animatedCount = useCounter(target ?? 0, isVisible, 1200);
  const displayCount = typeof target === 'number' ? animatedCount : null;
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [isVisible, delay]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.2em',
        opacity: show ? 1 : 0,
        transform: show ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 0.5s ease, transform 0.5s ease',
        minWidth: '70px',
      }}
    >
      <span style={{ fontSize: '1.4em' }}>{icon}</span>
      <span
        style={{
          color: '#fff',
          fontWeight: 700,
          fontSize: 'clamp(1.1em, 3vw, 1.4em)',
          lineHeight: 1,
        }}
      >
        {displayCount !== null ? displayCount : value}
      </span>
      <span
        style={{
          color: 'rgba(255,255,255,0.6)',
          fontSize: '0.72em',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          textAlign: 'center',
          lineHeight: 1.2,
        }}
      >
        {label}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// HomePage — outer wrapper handles auth redirect with zero flicker.
// The heavy landing page (with 9 IntersectionObservers) is only mounted
// when the user is definitely NOT logged in, so there is no intermediate
// render that could cause a flash.
// ---------------------------------------------------------------------------

export default observer(function HomePage() {
  const { userStore } = useStore();

  if (userStore.isLoggedIn) {
    return <Navigate to="/playground" replace />;
  }

  return <LandingPage />;
});

// ---------------------------------------------------------------------------
// LandingPage — only rendered for guests
// ---------------------------------------------------------------------------

const LandingPage = observer(function LandingPage() {
  const { t } = useTranslation();
  const { modalStore } = useStore();
  const [loaded, setLoaded] = useState(false);
  const [heroStatsVisible, setHeroStatsVisible] = useState(false);

  useEffect(() => {
    setLoaded(true);
    const timer = setTimeout(() => setHeroStatsVisible(true), 600);
    return () => clearTimeout(timer);
  }, []);

  const heroVisible = loaded;

  const section1 = useIntersectionObserver();
  const section2 = useIntersectionObserver();
  const section3 = useIntersectionObserver();
  const section4 = useIntersectionObserver();
  const section5 = useIntersectionObserver();
  const section6 = useIntersectionObserver();
  const section7 = useIntersectionObserver();
  const section8 = useIntersectionObserver();
  const section9 = useIntersectionObserver();

  const steps = [
    {
      number: '01',
      titleKey: 'homePage.howItWorks.step1.title',
      descKey: 'homePage.howItWorks.step1.desc',
    },
    {
      number: '02',
      titleKey: 'homePage.howItWorks.step2.title',
      descKey: 'homePage.howItWorks.step2.desc',
    },
    {
      number: '03',
      titleKey: 'homePage.howItWorks.step3.title',
      descKey: 'homePage.howItWorks.step3.desc',
    },
  ];

  const filterFeatures = [
    {
      icon: 'map marker alternate',
      titleKey: 'homePage.filters.feature1.title',
      descKey: 'homePage.filters.feature1.desc',
    },
    {
      icon: 'calendar outline',
      titleKey: 'homePage.filters.feature2.title',
      descKey: 'homePage.filters.feature2.desc',
    },
    {
      icon: 'universal access',
      titleKey: 'homePage.filters.feature3.title',
      descKey: 'homePage.filters.feature3.desc',
    },
    {
      icon: 'search',
      titleKey: 'homePage.filters.feature4.title',
      descKey: 'homePage.filters.feature4.desc',
    },
    {
      icon: 'lock',
      titleKey: 'homePage.filters.feature5.title',
      descKey: 'homePage.filters.feature5.desc',
    },
    {
      icon: 'user plus',
      titleKey: 'homePage.filters.feature6.title',
      descKey: 'homePage.filters.feature6.desc',
    },
  ];

  const venueFeatures = [
    {
      icon: 'calendar alternate outline',
      titleKey: 'homePage.venues.feature1.title',
      descKey: 'homePage.venues.feature1.desc',
    },
    {
      icon: 'search',
      titleKey: 'homePage.venues.feature2.title',
      descKey: 'homePage.venues.feature2.desc',
    },
    {
      icon: 'building outline',
      titleKey: 'homePage.venues.feature3.title',
      descKey: 'homePage.venues.feature3.desc',
    },
  ];

  const communityFeatures = [
    {
      icon: 'star',
      titleKey: 'homePage.community.feature1.title',
      descKey: 'homePage.community.feature1.desc',
    },
    {
      icon: 'comment outline',
      titleKey: 'homePage.community.feature2.title',
      descKey: 'homePage.community.feature2.desc',
    },
    {
      icon: 'chart line',
      titleKey: 'homePage.community.feature3.title',
      descKey: 'homePage.community.feature3.desc',
    },
  ];

  const cyclingPills = [
    t('homePage.hero.pill1'),
    t('homePage.hero.pill2'),
    t('homePage.hero.pill3'),
    t('homePage.hero.pill4'),
  ];

  const trustBadges = [
    { icon: '✅', textKey: 'homePage.hero.trust1' },
    { icon: '📱', textKey: 'homePage.hero.trust2' },
    { icon: '🆓', textKey: 'homePage.hero.trust3' },
  ];

  const neverMissKeys = ['text1', 'text2', 'text3'] as const;

  return (
    <>
      <Helmet>
        <title>{t('homePage.meta.title')}</title>
        <meta name="description" content={t('homePage.meta.description')} />
        <meta name="keywords" content={t('homePage.meta.keywords')} />
        <meta property="og:title" content={t('homePage.meta.title')} />
        <meta property="og:description" content={t('homePage.meta.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sportizan.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('homePage.meta.title')} />
        <meta name="twitter:description" content={t('homePage.meta.description')} />
        <link rel="canonical" href="https://sportizan.com" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Sportizan",
            "applicationCategory": "SportsApplication",
            "operatingSystem": "Web",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
            "description": "${t('homePage.meta.description')}",
            "url": "https://sportizan.com"
          }
        `}</script>
      </Helmet>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInLeft {
          from { opacity: 0; transform: translateX(-50px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInRight {
          from { opacity: 0; transform: translateX(50px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.8); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(60px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50%      { transform: translateY(-10px); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.05); opacity: 0.8; }
        }
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }
        @keyframes gradientShift {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes heroPulseRing {
          0%   { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255,255,255,0.18); }
          70%  { transform: scale(1);    box-shadow: 0 0 0 18px rgba(255,255,255,0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255,255,255,0); }
        }
        @keyframes statSeparatorGlow {
          0%, 100% { opacity: 0.15; }
          50%      { opacity: 0.35; }
        }

        /* ── Page wrapper ── */
        .sportizan-page {
          background: linear-gradient(
            135deg,
            rgb(30, 55, 140) 0%,
            rgb(38, 155, 196) 50%,
            rgb(35, 185, 185) 100%
          );
          background-size: 200% 200%;
          animation: gradientShift 15s ease infinite;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          width: 100%;
          margin: 0;
          padding: 0;
          overflow-x: hidden;
        }

        /* ── Animation utilities ── */
        .animate-fadeInUp    { opacity: 0; animation: fadeInUp    0.8s ease forwards; }
        .animate-fadeInDown  { opacity: 0; animation: fadeInDown  0.8s ease forwards; }
        .animate-fadeInLeft  { opacity: 0; animation: fadeInLeft  0.8s ease forwards; }
        .animate-fadeInRight { opacity: 0; animation: fadeInRight 0.8s ease forwards; }
        .animate-scaleIn     { opacity: 0; animation: scaleIn     0.6s ease forwards; }
        .animate-slideInUp   { opacity: 0; animation: slideInUp   0.8s ease forwards; }
        .animate-float       { animation: float 3s ease-in-out infinite; }
        .animate-pulse-slow  { animation: pulse 3s ease-in-out infinite; }
        .animate-bounce      { animation: bounce 2s infinite; }

        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
        .delay-600 { animation-delay: 0.6s; }
        .delay-700 { animation-delay: 0.7s; }
        .delay-800 { animation-delay: 0.8s; }

        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.3s; }
        .stagger-4 { animation-delay: 0.4s; }
        .stagger-5 { animation-delay: 0.5s; }
        .stagger-6 { animation-delay: 0.6s; }

        /* ── Glass card ── */
        .glass-card {
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 16px;
          padding: 2em;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }
        .glass-card::before {
          content: '';
          position: absolute;
          top: 0; left: -100%;
          width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
          transition: left 0.5s ease;
        }
        .glass-card:hover::before { left: 100%; }
        .glass-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px rgba(0,0,0,0.2), 0 0 30px rgba(255,255,255,0.1);
          border-color: rgba(255,255,255,0.4);
          background: rgba(255,255,255,0.15);
        }

        /* ── Step number ── */
        .step-number {
          width: 60px; height: 60px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05));
          border: 2px solid rgba(255,255,255,0.5);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 1em;
          color: #fff;
          font-size: 1.2em; font-weight: 700;
          transition: all 0.4s ease;
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }
        .glass-card:hover .step-number {
          transform: scale(1.1) rotate(5deg);
          background: linear-gradient(135deg, rgba(255,255,255,0.3), rgba(255,255,255,0.1));
          box-shadow: 0 0 20px rgba(255,255,255,0.3);
        }

        /* ── Sport badge ── */
        .sport-badge {
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(5px);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 50px;
          padding: 0.75em 1.5em;
          display: flex; align-items: center; gap: 0.5em;
          font-size: 1em; color: #fff; font-weight: 500;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          position: relative; overflow: hidden;
        }
        .sport-badge::after {
          content: '';
          position: absolute; inset: 0;
          background: radial-gradient(circle at center, rgba(255,255,255,0.3), transparent);
          opacity: 0; transition: opacity 0.3s ease;
        }
        .sport-badge:hover {
          transform: translateY(-5px) scale(1.05);
          background: rgba(255,255,255,0.2);
          border-color: rgba(255,255,255,0.4);
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }
        .sport-badge:hover::after { opacity: 1; }

        /* ── Hero buttons ── */
        .hero-button {
          position: relative; overflow: hidden;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .hero-button::before {
          content: '';
          position: absolute; top: 50%; left: 50%;
          width: 0; height: 0; border-radius: 50%;
          background: rgba(255,255,255,0.3);
          transform: translate(-50%, -50%);
          transition: width 0.6s ease, height 0.6s ease;
        }
        .hero-button:hover::before { width: 300px; height: 300px; }
        .hero-button:hover {
          transform: translateY(-3px) !important;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3) !important;
        }

        /* ── Primary CTA pulsing ring ── */
        .hero-button-primary {
          animation: heroPulseRing 2.4s ease-out infinite;
        }

        /* ── Feature icon ── */
        .feature-icon-wrapper { transition: all 0.4s ease; }
        .glass-card:hover .feature-icon-wrapper { transform: scale(1.2) rotate(5deg); }

        /* ── Section divider ── */
        .section-divider { position: relative; }
        .section-divider::after {
          content: '';
          position: absolute; bottom: -10px; left: 50%;
          transform: translateX(-50%);
          width: 60px; height: 3px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
          border-radius: 2px;
        }

        /* ── Check items ── */
        .check-item { transition: all 0.3s ease; }
        .check-item:hover {
          transform: translateX(10px);
          background: rgba(255,255,255,0.05);
          border-radius: 8px;
          padding: 0.6em 1em;
          margin: 0 -1em;
        }

        /* ── CTA button ── */
        .cta-button {
          background: linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05)) !important;
          border: 2px solid rgba(255,255,255,0.3) !important;
          position: relative; overflow: hidden;
          transition: all 0.4s ease !important;
        }
        .cta-button::after {
          content: '';
          position: absolute;
          top: -50%; left: -50%;
          width: 200%; height: 200%;
          background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%);
          transform: rotate(45deg) translateX(-100%);
          transition: transform 0.6s ease;
        }
        .cta-button:hover::after { transform: rotate(45deg) translateX(100%); }
        .cta-button:hover {
          transform: translateY(-3px) scale(1.02) !important;
          box-shadow: 0 15px 40px rgba(0,0,0,0.3), 0 0 30px rgba(255,255,255,0.1) !important;
          border-color: rgba(255,255,255,0.5) !important;
        }

        /* ── How-it-works strip ── */
        .how-strip {
          display: flex;
          justify-content: center;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 0;
          margin-top: 2em;
        }
        .how-strip-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 0 1.4em;
          opacity: 0;
          transition: opacity 0.5s ease, transform 0.5s ease;
          transform: translateY(10px);
        }
        .how-strip-item.visible {
          opacity: 1;
          transform: translateY(0);
        }
        .how-strip-item + .how-strip-item {
          border-left: 1px solid rgba(255,255,255,0.18);
        }
        .how-strip-num {
          width: 32px; height: 32px;
          border-radius: 50%;
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.35);
          display: flex; align-items: center; justify-content: center;
          color: #fff; font-size: 0.75em; font-weight: 700;
          margin-bottom: 0.5em;
          transition: all 0.3s ease;
        }
        .how-strip-item:hover .how-strip-num {
          background: rgba(255,255,255,0.25);
          transform: scale(1.15);
        }

        /* ── Social proof separator ── */
        .stat-separator {
          width: 1px; height: 36px;
          background: rgba(255,255,255,0.2);
          animation: statSeparatorGlow 3s ease-in-out infinite;
          align-self: center;
        }

        /* ── Reduced motion ── */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      <div className="sportizan-page">

        {/* ── HERO ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '55vh',
            padding: '4em 1em 2em',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          <Container text style={{ maxWidth: '620px', padding: '0 1em', position: 'relative', zIndex: 1 }}>

            {/* App name */}
            <Header
              as='h1'
              inverted
              className={heroVisible ? 'animate-fadeInDown' : ''}
              style={{
                fontSize: 'clamp(2em, 8vw, 4em)',
                fontWeight: 'normal',
                marginBottom: '0.1em',
                lineHeight: '1.2',
                textShadow: '0 2px 20px rgba(0,0,0,0.2)',
              }}
            >
              {t('homePage.appName')}
            </Header>

            {/* Tagline */}
            <Header
              as='h2'
              inverted
              className={heroVisible ? 'animate-fadeInUp delay-200' : ''}
              style={{
                fontSize: 'clamp(1.05em, 3.5vw, 1.45em)',
                fontWeight: 'normal',
                marginTop: '0.4em',
                marginBottom: '1.1em',
                lineHeight: '1.4',
                color: 'rgba(255,255,255,0.88)',
                opacity: heroVisible ? 1 : 0,
              }}
            >
              {t('homePage.hero.tagline')}
            </Header>

            {/* Cycling benefit pills */}
            <div
              className={heroVisible ? 'animate-fadeInUp delay-300' : ''}
              style={{ opacity: heroVisible ? 1 : 0 }}
            >
              <CyclingPills pills={cyclingPills} />
            </div>

            {/* CTA buttons */}
            <div
              className={heroVisible ? 'animate-fadeInUp delay-400' : ''}
              style={{
                marginTop: '1em',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.85em',
                width: '100%',
                padding: '0 1em',
                opacity: heroVisible ? 1 : 0,
              }}
            >
              <Button
                as='span'
                onClick={() => modalStore.openModal(<RegisterForm />)}
                size='huge'
                inverted
                primary
                className="hero-button hero-button-primary"
                style={{
                  width: '100%',
                  maxWidth: '320px',
                  fontSize: 'clamp(1em, 3vw, 1.15em)',
                  padding: '1em 1.5em',
                  borderRadius: '50px',
                  fontWeight: 700,
                }}
              >
                {t('homePage.hero.registerCta')} {t('homePage.registerButton')}
              </Button>

              <Button
                as='span'
                onClick={() => modalStore.openModal(<LoginForm />)}
                size='large'
                inverted
                className="hero-button"
                style={{
                  width: '100%',
                  maxWidth: '320px',
                  fontSize: 'clamp(0.9em, 2.5vw, 1em)',
                  padding: '0.85em 1.5em',
                  borderRadius: '50px',
                  opacity: 0.85,
                }}
              >
                {t('homePage.loginButton')}
              </Button>
            </div>

            {/* Trust badges */}
            <div
              className={heroVisible ? 'animate-fadeInUp delay-600' : ''}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1em',
                marginTop: '1.4em',
                opacity: heroVisible ? 1 : 0,
              }}
            >
              {trustBadges.map(({ icon, textKey }) => (
                <span
                  key={textKey}
                  style={{
                    color: 'rgba(255,255,255,0.65)',
                    fontSize: '0.8em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {icon} {t(textKey)}
                </span>
              ))}
            </div>

            {/* How it works strip */}
            <div className="how-strip" style={{ marginTop: '2.4em' }}>
              {steps.map((step, i) => (
                <div
                  key={step.number}
                  className={`how-strip-item${heroVisible ? ' visible' : ''}`}
                  style={{ transitionDelay: `${0.7 + i * 0.15}s` }}
                >
                  <div className="how-strip-num">{step.number}</div>
                  <span
                    style={{
                      color: 'rgba(255,255,255,0.8)',
                      fontSize: '0.8em',
                      maxWidth: '110px',
                      lineHeight: 1.35,
                      fontWeight: 500,
                    }}
                  >
                    {t(step.titleKey)}
                  </span>
                </div>
              ))}
            </div>

            {/* Social proof counters */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0',
                marginTop: '2.4em',
                padding: '1.2em 1.5em',
                background: 'rgba(0,0,0,0.15)',
                borderRadius: '16px',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(8px)',
                flexWrap: 'wrap',
                rowGap: '1em',
              }}
            >
              <StatBadge icon="🏅" value="50+"  label={t('homePage.hero.stat1')} isVisible={heroStatsVisible} delay={0}   />
              <div className="stat-separator" />
              <StatBadge icon="🏙️" value="+"    label={t('homePage.hero.stat2')} isVisible={heroStatsVisible} delay={150} />
              <div className="stat-separator" />
              <StatBadge icon="👥" value={30}   label={t('homePage.hero.stat3')} isVisible={heroStatsVisible} target={30} delay={300} />
              <div className="stat-separator" />
              <StatBadge icon="🆓" value="0€"   label={t('homePage.hero.stat4')} isVisible={heroStatsVisible} delay={450} />
            </div>
          </Container>
        </div>

        {/* Scroll hint */}
        <div
          className={heroVisible ? 'animate-bounce' : ''}
          style={{
            textAlign: 'center',
            paddingBottom: '2em',
            marginTop: '-0.5em',
            opacity: heroVisible ? 0.4 : 0,
            transition: 'opacity 1s ease 1.2s',
          }}
        >
          <Icon name='chevron down' inverted style={{ fontSize: '1.4em' }} />
        </div>

        {/* ── SECTION 1: Intro ── */}
        <div ref={section1.ref}>
          <Container text style={{ padding: '4em 2em' }}>
            <Divider inverted horizontal className="section-divider">
              <Header
                as='h4'
                inverted
                className={section1.isVisible ? 'animate-fadeInUp' : ''}
                style={{ opacity: 0.6, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.8em' }}
              >
                {t('homePage.appName')}
              </Header>
            </Divider>
            <div className={`glass-card ${section1.isVisible ? 'animate-slideInUp delay-200' : ''}`} style={{ marginTop: '2em' }}>
              <Header
                as='h2'
                inverted
                className={section1.isVisible ? 'animate-fadeInUp delay-300' : ''}
                style={{ fontSize: 'clamp(1.4em, 4vw, 2em)', marginBottom: '0.7em' }}
              >
                {t('homePage.intro.title')}
              </Header>
              <p
                className={section1.isVisible ? 'animate-fadeInUp delay-400' : ''}
                style={{ fontSize: 'clamp(1em, 2.5vw, 1.1em)', color: 'rgba(255,255,255,0.85)', lineHeight: '1.75', marginBottom: '0.9em' }}
              >
                {t('homePage.intro.text1')}
              </p>
              <p
                className={section1.isVisible ? 'animate-fadeInUp delay-500' : ''}
                style={{ fontSize: 'clamp(1em, 2.5vw, 1.1em)', color: 'rgba(255,255,255,0.85)', lineHeight: '1.75', marginBottom: '0.9em' }}
              >
                {t('homePage.intro.text2')}
              </p>
              <p
                className={section1.isVisible ? 'animate-fadeInUp delay-600' : ''}
                style={{ fontSize: 'clamp(1em, 2.5vw, 1.1em)', color: '#fff', fontWeight: '600', lineHeight: '1.75', marginBottom: '1.2em' }}
              >
                {t('homePage.intro.text3')}
              </p>
              <span
                className={`sport-badge ${section1.isVisible ? 'animate-scaleIn delay-700' : ''}`}
                style={{ display: 'inline-flex' }}
              >
                <Icon name='check' style={{ margin: 0 }} /> {t('homePage.noBrowserDownload')}
              </span>
            </div>
          </Container>
        </div>

        {/* ── SECTION 2: How it works (full cards) ── */}
        <div ref={section2.ref}>
          <Container style={{ padding: '4em 2em' }}>
            <Divider inverted horizontal className="section-divider">
              <Header
                as='h4'
                inverted
                className={section2.isVisible ? 'animate-fadeInUp' : ''}
                style={{ opacity: 0.6, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.8em' }}
              >
                {t('homePage.howItWorks.title')}
              </Header>
            </Divider>
            <Grid stackable columns={3} style={{ marginTop: '1.5em' }}>
              {steps.map((step, index) => (
                <Grid.Column key={step.number} textAlign='center'>
                  <div
                    className={`glass-card ${section2.isVisible ? `animate-slideInUp stagger-${index + 1}` : ''}`}
                    style={{ height: '100%' }}
                  >
                    <div className="step-number">{step.number}</div>
                    <Header
                      as='h3'
                      inverted
                      className={section2.isVisible ? `animate-fadeInUp stagger-${index + 2}` : ''}
                      style={{ marginBottom: '0.5em', fontSize: '1.1em' }}
                    >
                      {t(step.titleKey)}
                    </Header>
                    <p style={{ color: 'rgba(255,255,255,0.8)', lineHeight: '1.65', fontSize: '0.97em', margin: 0 }}>
                      {t(step.descKey)}
                    </p>
                  </div>
                </Grid.Column>
              ))}
            </Grid>
          </Container>
        </div>

        {/* ── SECTION 3: Never miss a game ── */}
        <div ref={section3.ref}>
          <Container text style={{ padding: '4em 2em' }}>
            <Divider inverted horizontal className="section-divider">
              <Header
                as='h4'
                inverted
                className={section3.isVisible ? 'animate-fadeInUp' : ''}
                style={{ opacity: 0.6, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.8em' }}
              >
                {t('homePage.neverMiss.title')}
              </Header>
            </Divider>
            <div
              className={`glass-card ${section3.isVisible ? 'animate-scaleIn delay-200' : ''}`}
              style={{ marginTop: '2em' }}
            >
              {neverMissKeys.map((key, i) => (
                <React.Fragment key={key}>
                  <div
                    className={`check-item ${section3.isVisible ? `animate-fadeInRight stagger-${i + 1}` : ''}`}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: '0.9em', padding: '0.6em 0' }}
                  >
                    <Icon
                      name='check circle outline'
                      size='large'
                      style={{ color: 'rgba(255,255,255,0.85)', marginTop: '1px', flexShrink: 0 }}
                    />
                    <p style={{ fontSize: 'clamp(1em, 2.5vw, 1.08em)', color: 'rgba(255,255,255,0.88)', lineHeight: '1.65', margin: 0 }}>
                      {t(`homePage.neverMiss.${key}`)}
                    </p>
                  </div>
                  {i < 2 && <Divider inverted style={{ margin: '0.2em 0', opacity: 0.15 }} />}
                </React.Fragment>
              ))}
            </div>
          </Container>
        </div>

        {/* ── SECTION 4: Filters ── */}
        <div ref={section4.ref}>
          <Container style={{ padding: '4em 2em' }}>
            <Divider inverted horizontal className="section-divider">
              <Header
                as='h4'
                inverted
                className={section4.isVisible ? 'animate-fadeInUp' : ''}
                style={{ opacity: 0.6, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.8em' }}
              >
                <Icon name='filter' style={{ margin: 0, marginRight: '0.4em' }} />
                {t('homePage.filters.title')}
              </Header>
            </Divider>
            <p
              className={section4.isVisible ? 'animate-fadeInUp delay-200' : ''}
              style={{ color: 'rgba(255,255,255,0.75)', textAlign: 'center', fontSize: '1.05em', marginTop: '1.2em', marginBottom: '1.8em', lineHeight: '1.6' }}
            >
              {t('homePage.filters.subtitle')}
            </p>
            <Grid stackable columns={3}>
              {filterFeatures.map((f, index) => (
                <Grid.Column key={f.titleKey}>
                  <div
                    className={`glass-card ${section4.isVisible ? `animate-slideInUp stagger-${(index % 3) + 1}` : ''}`}
                    style={{ display: 'flex', gap: '1em', alignItems: 'flex-start', height: '100%' }}
                  >
                    <div className="feature-icon-wrapper">
                      <Icon
                        name={f.icon as any}
                        size='large'
                        style={{ color: 'rgba(255,255,255,0.75)', marginTop: '2px', flexShrink: 0 }}
                      />
                    </div>
                    <div>
                      <Header as='h4' inverted style={{ marginBottom: '0.3em', fontSize: '1.05em' }}>
                        {t(f.titleKey)}
                      </Header>
                      <p style={{ color: 'rgba(255,255,255,0.78)', lineHeight: '1.6', fontSize: '0.96em', margin: 0 }}>
                        {t(f.descKey)}
                      </p>
                    </div>
                  </div>
                </Grid.Column>
              ))}
            </Grid>
          </Container>
        </div>

        {/* ── SECTION 5: Sports ── */}
        <div ref={section5.ref}>
          <Container text style={{ padding: '4em 2em' }}>
            <Divider inverted horizontal className="section-divider">
              <Header
                as='h4'
                inverted
                className={section5.isVisible ? 'animate-fadeInUp' : ''}
                style={{ opacity: 0.6, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.8em' }}
              >
                {t('homePage.sportsSection.title')}
              </Header>
            </Divider>
            <div
              className={`glass-card ${section5.isVisible ? 'animate-scaleIn delay-200' : ''}`}
              style={{ marginTop: '2em', textAlign: 'center' }}
            >
              <p
                className={section5.isVisible ? 'animate-fadeInUp delay-300' : ''}
                style={{ fontSize: 'clamp(1.1em, 3vw, 1.35em)', color: '#fff', fontWeight: 600, lineHeight: '1.5', marginBottom: '0.5em' }}
              >
                {t('homePage.sportsSection.headline')}
              </p>
              <p
                className={section5.isVisible ? 'animate-fadeInUp delay-400' : ''}
                style={{ fontSize: 'clamp(0.95em, 2.5vw, 1.05em)', color: 'rgba(255,255,255,0.75)', lineHeight: '1.7', margin: 0 }}
              >
                {t('homePage.sportsSection.subtext')}
              </p>
            </div>
          </Container>
        </div>

        {/* ── SECTION 6: Community / ratings ── */}
        <div ref={section6.ref}>
          <Container style={{ padding: '4em 2em' }}>
            <Divider inverted horizontal className="section-divider">
              <Header
                as='h4'
                inverted
                className={section6.isVisible ? 'animate-fadeInUp' : ''}
                style={{ opacity: 0.6, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.8em' }}
              >
                <Icon name='trophy' style={{ margin: 0, marginRight: '0.4em' }} />
                {t('homePage.community.dividerLabel')}
              </Header>
            </Divider>
            <div
              className={`glass-card ${section6.isVisible ? 'animate-slideInUp delay-200' : ''}`}
              style={{ marginTop: '2em', marginBottom: '1.5em' }}
            >
              <Header
                as='h2'
                inverted
                className={section6.isVisible ? 'animate-fadeInUp delay-300' : ''}
                style={{ fontSize: 'clamp(1.4em, 4vw, 2em)', marginBottom: '0.5em' }}
              >
                {t('homePage.community.title')}
              </Header>
              <p
                className={section6.isVisible ? 'animate-fadeInUp delay-400' : ''}
                style={{ fontSize: 'clamp(1em, 2.5vw, 1.08em)', color: 'rgba(255,255,255,0.85)', lineHeight: '1.75', margin: 0 }}
              >
                {t('homePage.community.subtitle')}
              </p>
            </div>
            <Grid stackable columns={3}>
              {communityFeatures.map((f, index) => (
                <Grid.Column key={f.titleKey}>
                  <div
                    className={`glass-card ${section6.isVisible ? `animate-slideInUp stagger-${index + 2}` : ''}`}
                    style={{ textAlign: 'center', height: '100%' }}
                  >
                    <div className="feature-icon-wrapper">
                      <Icon
                        name={f.icon as any}
                        size='big'
                        style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '0.6em' }}
                      />
                    </div>
                    <Header as='h4' inverted style={{ marginBottom: '0.4em', fontSize: '1.05em' }}>
                      {t(f.titleKey)}
                    </Header>
                    <p style={{ color: 'rgba(255,255,255,0.78)', lineHeight: '1.6', fontSize: '0.95em', margin: 0 }}>
                      {t(f.descKey)}
                    </p>
                  </div>
                </Grid.Column>
              ))}
            </Grid>
          </Container>
        </div>

        {/* ── SECTION 7: Venue owners ── */}
        <div ref={section7.ref}>
          <Container style={{ padding: '4em 2em' }}>
            <Divider inverted horizontal className="section-divider">
              <Header
                as='h4'
                inverted
                className={section7.isVisible ? 'animate-fadeInUp' : ''}
                style={{ opacity: 0.6, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.8em' }}
              >
                <Icon name='building outline' style={{ margin: 0, marginRight: '0.4em' }} />
                {t('homePage.venues.dividerLabel')}
              </Header>
            </Divider>
            <div
              className={`glass-card ${section7.isVisible ? 'animate-slideInUp delay-200' : ''}`}
              style={{ marginTop: '2em', marginBottom: '1.5em' }}
            >
              <Header
                as='h2'
                inverted
                className={section7.isVisible ? 'animate-fadeInUp delay-300' : ''}
                style={{ fontSize: 'clamp(1.4em, 4vw, 2em)', marginBottom: '0.5em' }}
              >
                {t('homePage.venues.title')}
              </Header>
              <p
                className={section7.isVisible ? 'animate-fadeInUp delay-400' : ''}
                style={{ fontSize: 'clamp(1em, 2.5vw, 1.08em)', color: 'rgba(255,255,255,0.85)', lineHeight: '1.75', margin: 0 }}
              >
                {t('homePage.venues.subtitle')}
              </p>
            </div>
            <Grid stackable columns={3}>
              {venueFeatures.map((f, index) => (
                <Grid.Column key={f.titleKey}>
                  <div
                    className={`glass-card ${section7.isVisible ? `animate-slideInUp stagger-${index + 2}` : ''}`}
                    style={{ textAlign: 'center', height: '100%' }}
                  >
                    <div className="feature-icon-wrapper">
                      <Icon
                        name={f.icon as any}
                        size='big'
                        style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '0.6em' }}
                      />
                    </div>
                    <Header as='h4' inverted style={{ marginBottom: '0.4em', fontSize: '1.05em' }}>
                      {t(f.titleKey)}
                    </Header>
                    <p style={{ color: 'rgba(255,255,255,0.78)', lineHeight: '1.6', fontSize: '0.95em', margin: 0 }}>
                      {t(f.descKey)}
                    </p>
                  </div>
                </Grid.Column>
              ))}
            </Grid>
          </Container>
        </div>

        {/* ── SECTION 8: Local SEO ── */}
        <div ref={section8.ref}>
          <Container text style={{ padding: '4em 2em' }}>
            <Divider inverted horizontal className="section-divider">
              <Header
                as='h4'
                inverted
                className={section8.isVisible ? 'animate-fadeInUp' : ''}
                style={{ opacity: 0.6, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.8em' }}
              >
                <Icon name='map marker alternate' style={{ margin: 0, marginRight: '0.4em' }} />
                {t('homePage.localSeo.title')}
              </Header>
            </Divider>
            <div
              className={`glass-card ${section8.isVisible ? 'animate-scaleIn delay-200' : ''}`}
              style={{ marginTop: '2em', textAlign: 'center' }}
            >
              <p
                className={section8.isVisible ? 'animate-fadeInUp delay-300' : ''}
                style={{ fontSize: 'clamp(1em, 2.5vw, 1.1em)', color: 'rgba(255,255,255,0.88)', lineHeight: '1.8', margin: 0 }}
              >
                {t('homePage.localSeo.text')}
              </p>
            </div>
          </Container>
        </div>

        {/* ── SECTION 9: CTA ── */}
        <div ref={section9.ref}>
          <Container text style={{ padding: '4em 2em', textAlign: 'center', paddingBottom: '5em' }}>
            <Divider inverted style={{ marginBottom: '2.5em' }} />
            <Header
              as='h2'
              inverted
              className={section9.isVisible ? 'animate-fadeInUp' : ''}
              style={{ fontSize: 'clamp(1.4em, 4vw, 2em)', marginBottom: '0.5em' }}
            >
              {t('homePage.cta.title')}
            </Header>
            <p
              className={section9.isVisible ? 'animate-fadeInUp delay-200' : ''}
              style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.05em', marginBottom: '1.8em' }}
            >
              {t('homePage.cta.subtitle')}
            </p>
            <Button
              as='span'
              onClick={() => modalStore.openModal(<RegisterForm />)}
              size='huge'
              inverted
              className={`cta-button ${section9.isVisible ? 'animate-scaleIn delay-400' : ''}`}
              style={{ fontSize: 'clamp(1em, 3vw, 1.2em)', padding: '1em 2.5em', borderRadius: '50px' }}
            >
              {t('homePage.hero.registerCta')}
            </Button>
          </Container>
        </div>

        {/* ── FOOTER ── */}
        <div
          style={{
            padding: '1.5em 1em',
            borderTop: '1px solid rgba(255,255,255,0.12)',
            backgroundColor: 'rgba(0,0,0,0.2)',
          }}
        >
          <Container textAlign='center'>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1.5em',
                fontSize: 'clamp(0.8em, 2.5vw, 0.9em)',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                {t('homePage.footer.copyright', { year: new Date().getFullYear() })}
              </span>
              <a
                href="/legal"
                style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.3s ease' }}
              >
                {t('homePage.footer.legalLink')}
              </a>
            </div>
          </Container>
        </div>

      </div>
    </>
  );
});